/**
 * EL TIPO DE CLAIM QUE SE VE AL LADO DEL NOMBRE.
 *
 * Espejo de `backend/src/lib/etiquetasClaim.ts`, que es quien MANDA: el servidor valida contra su
 * lista y rechaza lo que no esté. Se duplica y no se comparte por el mismo motivo que el resto de
 * las constantes del repo: el frontend no importa del backend. Si las dos se desincronizan, el
 * desplegable ofrece algo que el backend rechaza — visible al instante, no en silencio.
 *
 * OJO: esta lista decía `['Roof','Water','Fire','Mitigation','CAT3','Hurricane']` y era MENTIRA
 * desde el 2026-08-26: la lista cerrada del backend es Roof/Water/Fire/Vandalism, así que elegir
 * "Mitigation", "CAT3" o "Hurricane" en la tarjeta del cliente o en Add Lead devolvía un error de
 * validación. Un desplegable que ofrece tres opciones que no se pueden guardar es peor que no
 * tener desplegable.
 *
 * La derivación (`derivarClaimType`) está explicada en el archivo del backend: `claim_type`
 * puesto a mano manda; si no, se lee el tipo que la oficina anota en el NOMBRE ("Gabor Halacsy -
 * water"); si no, `type_of_loss`; y si no, Roof.
 */
export const CLAIM_TYPES = ['Roof', 'Water', 'Fire', 'Vandalism', 'Mitigation'] as const;
export type ClaimType = (typeof CLAIM_TYPES)[number];

const COLORS: Record<string, { bg: string; fg: string }> = {
  roof: { bg: '#e0e7ff', fg: '#3730a3' },
  water: { bg: '#dbeafe', fg: '#1e40af' },
  fire: { bg: '#fee2e2', fg: '#b91c1c' },
  vandalism: { bg: '#f3e8ff', fg: '#6b21a8' },
  // Valores históricos que siguen en la base (1 "Wind") o que llegan del intake sin normalizar.
  mitigation: { bg: '#dcfce7', fg: '#166534' },
  cat3: { bg: '#fef3c7', fg: '#92400e' },
  hurricane: { bg: '#fce7f3', fg: '#9d174d' },
};

export function claimTypeStyle(type: string | null | undefined): { background: string; color: string } {
  const c = COLORS[(type ?? '').toLowerCase()] ?? { bg: '#eef1f4', fg: '#475569' };
  return { background: c.bg, color: c.fg };
}

/** Palabras que delatan cada tipo. El orden importa: gana el primero que aparezca. */
const PISTAS: ReadonlyArray<readonly [ClaimType, RegExp]> = [
  // Mitigation primero (dueño 2026-10-20: "si tiene mitigation que diga mitigation").
  ['Mitigation', /\b(mitigation|mitigaci[oó]n|tarp\w*)\b/i],
  ['Water', /\b(water|agua|flood|leak|pipe\s*burst|plumb\w*|freez\w*)\b/i],
  ['Fire', /\b(fire|smoke|fuego|humo|incendio)\b/i],
  ['Vandalism', /\b(vandal\w*|theft|burglar\w*|robo)\b/i],
  ['Roof', /\b(roof|techo|wind|hail|storm|hurricane|tornado|granizo)\b/i],
];

const buscarPista = (texto: string): ClaimType | null =>
  PISTAS.find(([, re]) => re.test(texto))?.[0] ?? null;

/**
 * Tipo escondido en el NOMBRE. Solo cuenta cuando la anotación se distingue de un apellido: va
 * después de un separador ("Gabor Halacsy - water", "Ledys Miranda (Roof)") o pegada a
 * claim/damage/supplement ("Fredy Dominguez Water claim"). Sin eso, "Truwork hailu", "Orlando
 * Adwaters" y "Water Shed Home LLC" —los tres reales, en prod— saldrían mal etiquetados.
 */
function deNombre(nombre: string): ClaimType | null {
  const n = nombre.trim();
  if (!n) return null;
  const corte = n.search(/[-–—(/,]/);
  const cola = corte >= 0 ? n.slice(corte + 1) : '';
  const porCola = cola ? buscarPista(cola) : null;
  if (porCola) return porCola;
  const frase = n.match(/([a-záéíóúñ]+)\s+(?:claim|damage|supplement)/i)?.[1];
  return frase ? buscarPista(frase) : null;
}

/**
 * Tipo a partir de `type_of_loss`. Manda el PRIMER término de la lista: en prod hay "Wind, water"
 * (5) y "Storm, water" — daños mixtos donde lo primero es la causa principal, y buscar en todo el
 * texto los llamaría Water solo porque Water se evalúa antes que Roof. Si el primer término no
 * dice nada ("Lightning", "Wnd", una fecha suelta), se mira el resto antes de rendirse.
 */
function dePerdida(perdida: string): ClaimType | null {
  const t = perdida.trim();
  if (!t) return null;
  const primero = t.split(/[,/&]|\band\b/i)[0]!.trim();
  return (primero ? buscarPista(primero) : null) ?? buscarPista(t);
}

export type ClaimTypeResuelto = {
  tipo: ClaimType;
  /** true = lo dedujo la app (nadie lo eligió): el chip se pinta más tenue. */
  auto: boolean;
  origen: 'set' | 'mitigation' | 'name' | 'loss' | 'default';
};

/** Fuente única del tipo que se PINTA. Ver el archivo del backend para el porqué de cada paso. */
export function derivarClaimType(claim: {
  claim_type?: string | null; homeowner_name?: string | null; type_of_loss?: string | null;
  /** Tiene estimado de mitigación (lo trae el claim y la lista de Claims): el chip dice Mitigation. */
  has_mitigation?: boolean | null;
}): ClaimTypeResuelto {
  const guardado = (claim.claim_type ?? '').trim();
  if (guardado) {
    const canon = CLAIM_TYPES.find((x) => x.toLowerCase() === guardado.toLowerCase().replace(/\s+claims?$/, '').trim());
    const tipo = canon ?? buscarPista(guardado);
    if (tipo) return { tipo, auto: false, origen: 'set' };
  }
  if (claim.has_mitigation) return { tipo: 'Mitigation', auto: true, origen: 'mitigation' };
  const porNombre = deNombre(claim.homeowner_name ?? '');
  if (porNombre) return { tipo: porNombre, auto: true, origen: 'name' };
  const porPerdida = dePerdida(claim.type_of_loss ?? '');
  if (porPerdida) return { tipo: porPerdida, auto: true, origen: 'loss' };
  return { tipo: 'Roof', auto: true, origen: 'default' };
}
